function _createForOfIteratorHelper(o, allowArrayLike) { var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"]; if (!it) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = it.call(o); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

import { warnOnce, SubscriptionManager, velocityPerSecond } from 'motion-utils';
import { time } from '../frameloop/sync-time.mjs';
import { frame } from '../frameloop/frame.mjs';
/**
 * Maximum time between the value of two frames, beyond which we
 * assume the velocity has since been 0.
 */

var MAX_VELOCITY_DELTA = 30;

var isFloat = function isFloat(value) {
  return !isNaN(parseFloat(value));
};

var collectMotionValues = {
  current: undefined
};
/**
 * `MotionValue` is used to track the state and velocity of motion values.
 *
 * @public
 */

var MotionValue = /*#__PURE__*/function () {
  /**
   * @param init - The initiating value
   * @param config - Optional configuration options
   *
   * -  `transformer`: A function to transform incoming values with.
   */
  function MotionValue(init) {
    var _this = this;

    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    _classCallCheck(this, MotionValue);

    /**
     * Tracks whether this value can output a velocity. Currently this is only true
     * if the value is numerical, but we might be able to widen the scope here and support
     * other value types.
     *
     * @internal
     */
    this.canTrackVelocity = null;
    /**
     * An object containing a SubscriptionManager for each active event.
     */

    this.events = {};

    this.updateAndNotify = function (v) {
      var render = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      var currentTime = time.now();
      /**
       * If we're updating the value during another frame or eventloop
       * than the previous frame, then the we set the previous frame value
       * to current.
       */

      if (_this.updatedAt !== currentTime) {
        _this.setPrevFrameValue();
      }

      _this.prev = _this.current;

      _this.setCurrent(v); // Update update subscribers


      if (_this.current !== _this.prev) {
        var _this$events$change;

        (_this$events$change = _this.events.change) === null || _this$events$change === void 0 ? void 0 : _this$events$change.notify(_this.current);

        if (_this.dependents) {
          var _iterator = _createForOfIteratorHelper(_this.dependents),
              _step;

          try {
            for (_iterator.s(); !(_step = _iterator.n()).done;) {
              var dependent = _step.value;
              dependent.dirty();
            }
          } catch (err) {
            _iterator.e(err);
          } finally {
            _iterator.f();
          }
        }
      } // Update render subscribers


      if (render) {
        var _this$events$renderRe;

        (_this$events$renderRe = _this.events.renderRequest) === null || _this$events$renderRe === void 0 ? void 0 : _this$events$renderRe.notify(_this.current);
      }
    };

    this.hasAnimated = false;
    this.setCurrent(init);
    this.owner = options.owner;
  }

  _createClass(MotionValue, [{
    key: "setCurrent",
    value: function setCurrent(current) {
      this.current = current;
      this.updatedAt = time.now();

      if (this.canTrackVelocity === null && current !== undefined) {
        this.canTrackVelocity = isFloat(this.current);
      }
    }
  }, {
    key: "setPrevFrameValue",
    value: function setPrevFrameValue() {
      var prevFrameValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : this.current;
      this.prevFrameValue = prevFrameValue;
      this.prevUpdatedAt = this.updatedAt;
    }
    /**
     * Adds a function that will be notified when the `MotionValue` is updated.
     *
     * It returns a function that, when called, will cancel the subscription.
     *
     * When calling `onChange` inside a React component, it should be wrapped with the
     * `useEffect` hook. As it returns an unsubscribe function, this should be returned
     * from the `useEffect` function to ensure you don't add duplicate subscribers..
     *
     * ```jsx
     * export const MyComponent = () => {
     *   const x = useMotionValue(0)
     *   const y = useMotionValue(0)
     *   const opacity = useMotionValue(1)
     *
     *   useEffect(() => {
     *     function updateOpacity() {
     *       const maxXY = Math.max(x.get(), y.get())
     *       const newOpacity = transform(maxXY, [0, 100], [1, 0])
     *       opacity.set(newOpacity)
     *     }
     *
     *     const unsubscribeX = x.on("change", updateOpacity)
     *     const unsubscribeY = y.on("change", updateOpacity)
     *
     *     return () => {
     *       unsubscribeX()
     *       unsubscribeY()
     *     }
     *   }, [])
     *
     *   return <motion.div style={{ x }} />
     * }
     * ```
     *
     * @param subscriber - A function that receives the latest value.
     * @returns A function that, when called, will cancel this subscription.
     *
     * @deprecated
     */

  }, {
    key: "onChange",
    value: function onChange(subscription) {
      if (process.env.NODE_ENV !== "production") {
        warnOnce(false, "value.onChange(callback) is deprecated. Switch to value.on(\"change\", callback).");
      }

      return this.on("change", subscription);
    }
  }, {
    key: "on",
    value: function on(eventName, callback) {
      var _this2 = this;

      if (!this.events[eventName]) {
        this.events[eventName] = new SubscriptionManager();
      }

      var unsubscribe = this.events[eventName].add(callback);

      if (eventName === "change") {
        return function () {
          unsubscribe();
          /**
           * If we have no more change listeners by the start
           * of the next frame, stop active animations.
           */

          frame.read(function () {
            if (!_this2.events.change.getSize()) {
              _this2.stop();
            }
          });
        };
      }

      return unsubscribe;
    }
  }, {
    key: "clearListeners",
    value: function clearListeners() {
      for (var eventManagers in this.events) {
        this.events[eventManagers].clear();
      }
    }
    /**
     * Attaches a passive effect to the `MotionValue`.
     */

  }, {
    key: "attach",
    value: function attach(passiveEffect, stopPassiveEffect) {
      this.passiveEffect = passiveEffect;
      this.stopPassiveEffect = stopPassiveEffect;
    }
    /**
     * Sets the state of the `MotionValue`.
     *
     * @remarks
     *
     * ```jsx
     * const x = useMotionValue(0)
     * x.set(10)
     * ```
     *
     * @param latest - Latest value to set.
     * @param render - Whether to notify render subscribers. Defaults to `true`
     *
     * @public
     */

  }, {
    key: "set",
    value: function set(v) {
      var render = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;

      if (!render || !this.passiveEffect) {
        this.updateAndNotify(v, render);
      } else {
        this.passiveEffect(v, this.updateAndNotify);
      }
    }
  }, {
    key: "setWithVelocity",
    value: function setWithVelocity(prev, current, delta) {
      this.set(current);
      this.prev = undefined;
      this.prevFrameValue = prev;
      this.prevUpdatedAt = this.updatedAt - delta;
    }
    /**
     * Set the state of the `MotionValue`, stopping any active animations,
     * effects, and resets velocity to `0`.
     */

  }, {
    key: "jump",
    value: function jump(v) {
      var endAnimation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
      this.updateAndNotify(v);
      this.prev = v;
      this.prevUpdatedAt = this.prevFrameValue = undefined;
      endAnimation && this.stop();
      if (this.stopPassiveEffect) this.stopPassiveEffect();
    }
  }, {
    key: "dirty",
    value: function dirty() {
      var _this$events$change2;

      (_this$events$change2 = this.events.change) === null || _this$events$change2 === void 0 ? void 0 : _this$events$change2.notify(this.current);
    }
  }, {
    key: "addDependent",
    value: function addDependent(dependent) {
      if (!this.dependents) {
        this.dependents = new Set();
      }

      this.dependents.add(dependent);
    }
  }, {
    key: "removeDependent",
    value: function removeDependent(dependent) {
      if (this.dependents) {
        this.dependents.delete(dependent);
      }
    }
    /**
     * Returns the latest state of `MotionValue`
     *
     * @returns - The latest state of `MotionValue`
     *
     * @public
     */

  }, {
    key: "get",
    value: function get() {
      if (collectMotionValues.current) {
        collectMotionValues.current.push(this);
      }

      return this.current;
    }
    /**
     * @public
     */

  }, {
    key: "getPrevious",
    value: function getPrevious() {
      return this.prev;
    }
    /**
     * Returns the latest velocity of `MotionValue`
     *
     * @returns - The latest velocity of `MotionValue`. Returns `0` if the state is non-numerical.
     *
     * @public
     */

  }, {
    key: "getVelocity",
    value: function getVelocity() {
      var currentTime = time.now();

      if (!this.canTrackVelocity || this.prevFrameValue === undefined || currentTime - this.updatedAt > MAX_VELOCITY_DELTA) {
        return 0;
      }

      var delta = Math.min(this.updatedAt - this.prevUpdatedAt, MAX_VELOCITY_DELTA); // Casts because of parseFloat's poor typing

      return velocityPerSecond(parseFloat(this.current) - parseFloat(this.prevFrameValue), delta);
    }
    /**
     * Registers a new animation to control this `MotionValue`. Only one
     * animation can drive a `MotionValue` at one time.
     *
     * ```jsx
     * value.start()
     * ```
     *
     * @param animation - A function that starts the provided animation
     */

  }, {
    key: "start",
    value: function start(startAnimation) {
      var _this3 = this;

      this.stop();
      return new Promise(function (resolve) {
        _this3.hasAnimated = true;
        _this3.animation = startAnimation(resolve);

        if (_this3.events.animationStart) {
          _this3.events.animationStart.notify();
        }
      }).then(function () {
        if (_this3.events.animationComplete) {
          _this3.events.animationComplete.notify();
        }

        _this3.clearAnimation();
      });
    }
    /**
     * Stop the currently active animation.
     *
     * @public
     */

  }, {
    key: "stop",
    value: function stop() {
      if (this.animation) {
        this.animation.stop();

        if (this.events.animationCancel) {
          this.events.animationCancel.notify();
        }
      }

      this.clearAnimation();
    }
    /**
     * Returns `true` if this value is currently animating.
     *
     * @public
     */

  }, {
    key: "isAnimating",
    value: function isAnimating() {
      return !!this.animation;
    }
  }, {
    key: "clearAnimation",
    value: function clearAnimation() {
      delete this.animation;
    }
    /**
     * Destroy and clean up subscribers to this `MotionValue`.
     *
     * The `MotionValue` hooks like `useMotionValue` and `useTransform` automatically
     * handle the lifecycle of the returned `MotionValue`, so this method is only necessary if you've manually
     * created a `MotionValue` via the `motionValue` function.
     *
     * @public
     */

  }, {
    key: "destroy",
    value: function destroy() {
      var _this$dependents, _this$events$destroy;

      (_this$dependents = this.dependents) === null || _this$dependents === void 0 ? void 0 : _this$dependents.clear();
      (_this$events$destroy = this.events.destroy) === null || _this$events$destroy === void 0 ? void 0 : _this$events$destroy.notify();
      this.clearListeners();
      this.stop();

      if (this.stopPassiveEffect) {
        this.stopPassiveEffect();
      }
    }
  }]);

  return MotionValue;
}();

function motionValue(init, options) {
  return new MotionValue(init, options);
}

export { MotionValue, collectMotionValues, motionValue };